import React, { useState, useRef, useEffect } from 'react';
import { testimonialsData } from '../../data/mock';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';

const Testimonials = () => {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [cardWidth, setCardWidth] = useState(300);

  // Update card width based on screen size
  useEffect(() => {
    const updateCardWidth = () => {
      if (window.innerWidth < 640) {
        setCardWidth(280);
      } else {
        setCardWidth(350);
      }
    };
    
    updateCardWidth();
    window.addEventListener('resize', updateCardWidth);
    return () => window.removeEventListener('resize', updateCardWidth);
  }, []);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = cardWidth + 24; // card width + gap
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="testimonials" className="bg-[#f8f6f3] py-16 sm:py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12">
          <p
            className="text-[#c9a96e] text-[10px] sm:text-[11px] tracking-[0.3em] uppercase mb-3 sm:mb-4"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Client Stories
          </p>
          <h2
            className="text-[#0a1628] text-2xl sm:text-3xl md:text-4xl font-light mb-3 sm:mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Trusted by Travellers
          </h2>
          <p
            className="text-[#4a5568] text-sm sm:text-base max-w-xl mx-auto font-light px-2"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Real experiences from our valued clients around the world
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div className="relative">
          {/* Left Arrow - Hidden on mobile */}
          {showLeftArrow && (
            <button
              onClick={() => scroll('left')}
              className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 lg:-translate-x-4 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white shadow-lg rounded-full items-center justify-center hover:bg-[#c9a96e] hover:text-white transition-all duration-300"
            >
              <ChevronLeft size={20} />
            </button>
          )}

          {/* Right Arrow - Hidden on mobile */}
          {showRightArrow && (
            <button
              onClick={() => scroll('right')}
              className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 lg:translate-x-4 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white shadow-lg rounded-full items-center justify-center hover:bg-[#c9a96e] hover:text-white transition-all duration-300"
            >
              <ChevronRight size={20} />
            </button>
          )}

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex gap-4 sm:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {testimonialsData.map((testimonial) => (
              <div
                key={testimonial.id}
                className="flex-shrink-0 w-[280px] sm:w-[350px] bg-white p-5 sm:p-8 group hover:-translate-y-1 transition-transform duration-300 shadow-sm snap-start"
              >
                {/* Rating */}
                <div className="flex gap-1 mb-4 sm:mb-5">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      size={12}
                      className="fill-[#c9a96e] text-[#c9a96e] sm:w-[14px] sm:h-[14px]"
                    />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p
                  className="text-[#0a1628] text-sm sm:text-base leading-relaxed mb-5 sm:mb-6 font-light italic min-h-[80px] sm:min-h-[100px]"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  "{testimonial.text}"
                </p>

                {/* Author Info */}
                <div className="border-t border-[#0a1628]/10 pt-4 sm:pt-5">
                  <p
                    className="text-[#0a1628] text-xs sm:text-sm font-medium mb-1"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {testimonial.name}
                  </p>
                  <p
                    className="text-[#4a5568] text-[10px] sm:text-xs mb-1"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {testimonial.location}
                  </p>
                  <p
                    className="text-[#c9a96e] text-[9px] sm:text-[10px] uppercase tracking-wider"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {testimonial.tripType}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll Indicator - Mobile friendly */}
        <div className="flex justify-center mt-6 sm:mt-8 gap-2">
          <p className="text-[#4a5568] text-xs">
            <span className="sm:hidden">Swipe to see more →</span>
            <span className="hidden sm:inline">Scroll to see more reviews →</span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
